import { useCallback } from 'react';

import { useUI } from '@apps/web/context/ui_provider';
import { useTrainingCoverage } from '@apps/web/features/training_coverage/api/training_coverage_queries';
import {
    type CoverageGapCaregiver,
    type TrainingCoverageResponse,
} from '@libraries/typescript/api_client';

// Cap the names spelled out in a chat draft so a large gap does not produce an
// unwieldy composer; the coordinator still sees the full list on the page.
const MAX_PROMPT_NAMES = 30;

function joinNames(caregivers: ReadonlyArray<CoverageGapCaregiver>): string {
    const names = caregivers.map((caregiver) => caregiver.name);
    const shown = names.slice(0, MAX_PROMPT_NAMES).join(', ');
    const overflow =
        names.length > MAX_PROMPT_NAMES
            ? `, and ${names.length - MAX_PROMPT_NAMES} more`
            : '';
    return `${shown}${overflow}`;
}

export function useTrainingCoverageDashboard({
    organizationId,
    isAdmin,
}: {
    readonly organizationId: string | null;
    readonly isAdmin: boolean;
}): {
    readonly isLoading: boolean;
    readonly error: unknown;
    readonly data: TrainingCoverageResponse | undefined;
    readonly isPreview: boolean;
    readonly canSeeContent: boolean;
    readonly readyAfterDays: number;
    readonly askPhoebeToTextMissingAvailability: () => void;
} {
    const { data, isLoading, error } = useTrainingCoverage({ organizationId });
    const { openChatWithComposerText } = useUI();

    const missingAvailability = data?.caregivers_missing_availability;
    const askPhoebeToTextMissingAvailability = useCallback(() => {
        if (!missingAvailability || missingAvailability.length === 0) {
            return;
        }
        openChatWithComposerText(
            `Text these caregivers we have no availability on file for and ask when they can work: ${joinNames(missingAvailability)}.`,
            'training_coverage',
        );
    }, [missingAvailability, openChatWithComposerText]);

    return {
        isLoading,
        error,
        data,
        isPreview: data ? isAdmin && !data.is_ready : false,
        canSeeContent: data ? data.is_ready || isAdmin : false,
        readyAfterDays: data?.ready_after_days ?? 3,
        askPhoebeToTextMissingAvailability,
    };
}
